import React, { useEffect } from 'react';
import { motion, Variants } from 'framer-motion';

const sectionVariants: Variants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};

const TermsPage: React.FC = () => {
  useEffect(() => {
    // Smooth scroll behavior for anchor links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const targetId = this.getAttribute('href')?.substring(1);
        if (!targetId) return;

        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          window.scrollTo({
            top: targetElement.offsetTop - 100, // Offset to account for sticky header
            behavior: 'smooth'
          });

          // Update URL hash without jumping
          window.history.pushState(null, '', `#${targetId}`);
        }
      });
    });
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden" style={{
      background: 'linear-gradient(135deg, #141019 0%, #1A1625 50%, #1F1A27 100%)',
    }}>
      {/* Background elements */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]"></div>
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-purple-500/10 blur-[170px] rounded-full"></div>
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-pink-500/10 blur-[170px] rounded-full"></div>

      {/* Decorative grid pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="h-full w-full" style={{
          backgroundImage: `linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(to right, #ffffff 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Decorative elements */}
      <div className="absolute top-[30%] left-[5%] opacity-20">
        <div className="w-32 h-[1px] bg-gradient-to-r from-[#F670C7] to-transparent transform rotate-45"></div>
        <div className="w-32 h-[1px] bg-gradient-to-r from-[#F670C7] to-transparent transform -rotate-45 mt-16"></div>
      </div>

      <div className="absolute right-[10%] bottom-[15%] opacity-20">
        <div className="rounded-full h-40 w-40 border border-dashed border-[#F670C7]/30"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rounded-full h-20 w-20 border border-[#F670C7]/50"></div>
      </div>

      <div className="container mx-auto px-4 py-16 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Header Section */}
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-block mb-4 px-4 py-1 bg-white/10 backdrop-blur-md rounded-full text-[#F670C7] text-sm font-medium"
            >
              Legal Information
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-5xl font-bold mb-6 text-white tracking-tight"
            >
              Terms and Conditions
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="text-white/70 text-lg max-w-2xl mx-auto"
            >
              The legal agreement between you and Developer Marketplace when using our services.
            </motion.p>
          </div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={sectionVariants}
            className="prose prose-invert prose-headings:font-medium prose-headings:mb-4 prose-p:mb-4 prose-p:text-white/80 prose-li:text-white/80 prose-li:mb-2 max-w-none backdrop-blur-sm bg-gradient-to-r from-[#1F1A27]/90 to-[#2A1E44]/90 p-8 md:p-10 rounded-2xl shadow-xl border border-white/10"
          >
            <div className="flex items-center justify-between mb-8 pb-6 border-b border-white/10">
              <p className="text-white text-lg m-0">
                Last updated: June 15, 2023
              </p>

              <div className="flex items-center space-x-3">
                <button onClick={() => window.print()} className="flex items-center space-x-2 text-white/70 hover:text-white transition">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                  </svg>
                  <span className="text-sm hidden md:inline">Print</span>
                </button>

                <button onClick={() => navigator.clipboard.writeText(window.location.href)} className="flex items-center space-x-2 text-white/70 hover:text-white transition">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <span className="text-sm hidden md:inline">Copy Link</span>
                </button>
              </div>
            </div>

            <div className="md:grid md:grid-cols-[250px_1fr] gap-8">
              {/* Table of Contents (Desktop) */}
              <div className="hidden md:block">
                <div className="sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto pr-4 pb-6 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent">
                  <h4 className="text-sm uppercase tracking-wider text-white/50 mb-4 font-medium">Contents</h4>
                  <nav className="space-y-2">
                    <a href="#introduction" className="block text-white/70 hover:text-[#F670C7] transition-colors">Introduction</a>
                    <a href="#accounts" className="block text-white/70 hover:text-[#F670C7] transition-colors">Accounts</a>
                    <a href="#intellectual-property" className="block text-white/70 hover:text-[#F670C7] transition-colors">Intellectual Property</a>
                    <a href="#user-content" className="block text-white/70 hover:text-[#F670C7] transition-colors">User Content</a>
                    <a href="#marketplace" className="block text-white/70 hover:text-[#F670C7] transition-colors">Marketplace Transactions</a>
                    <a href="#termination" className="block text-white/70 hover:text-[#F670C7] transition-colors">Termination</a>
                    <a href="#liability" className="block text-white/70 hover:text-[#F670C7] transition-colors">Limitation of Liability</a>
                    <a href="#governing-law" className="block text-white/70 hover:text-[#F670C7] transition-colors">Governing Law</a>
                    <a href="#changes" className="block text-white/70 hover:text-[#F670C7] transition-colors">Changes to Terms</a>
                    <a href="#contact" className="block text-white/70 hover:text-[#F670C7] transition-colors">Contact Us</a>
                  </nav>

                  <div className="mt-12 p-6 bg-gradient-to-br from-[#7F56D9]/20 to-[#F670C7]/20 rounded-xl border border-white/10">
                    <div className="flex items-center space-x-3 mb-3">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#F670C7]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <h5 className="font-medium text-white m-0 text-base">Your Agreement</h5>
                    </div>
                    <p className="text-sm text-white/70 m-0">By using our platform, you agree to these terms. Please read them carefully.</p>
                  </div>
                </div>
              </div>

              {/* Main Content */}
              <div>
                <section id="introduction">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Introduction</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    These Terms and Conditions govern your use of Developer Marketplace, accessible at developermarketplace.com, and constitute a legally binding agreement between you and Developer Marketplace.
                  </p>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any part of the terms, then you may not access the Service.
                  </p>

                  <div className="bg-white/5 p-5 rounded-xl my-6 border border-white/10">
                    <div className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#F670C7] mt-1 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-white/90 text-sm m-0">
                        <strong className="text-white">Important:</strong> These terms include provisions that affect your legal rights, including limitation of liability, dispute resolution provisions, and a class action waiver.
                      </p>
                    </div>
                  </div>
                </section>

                <section id="accounts">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Accounts</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    When you create an account with us, you must provide accurate, complete, and up-to-date information. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account on our Service.
                  </p>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    You are responsible for safeguarding the password that you use to access the Service and for any activities or actions under your password, whether your password is with our Service or a third-party service.
                  </p>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
                  </p>
                </section>

                <section id="intellectual-property">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Intellectual Property</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    The Service and its original content, features, and functionality are and will remain the exclusive property of Developer Marketplace and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries.
                  </p>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of Developer Marketplace.
                  </p>
                </section>

                <section id="user-content">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">User Content</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    Our Service allows you to post, link, store, share and otherwise make available certain information, text, graphics, videos, or other material. You are responsible for the content that you post on or through the Service, including its legality, reliability, and appropriateness.
                  </p>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    By posting content on or through the Service, you represent and warrant that:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">The content is yours (you own it) or you have the right to use it and grant us the rights and license as provided in these Terms.</li>
                    <li className="text-white/90 leading-relaxed">The posting of your content on or through the Service does not violate the privacy rights, publicity rights, copyrights, contract rights or any other rights of any person.</li>
                  </ul>
                </section>

                <section id="marketplace">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Marketplace Transactions</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    Developer Marketplace serves as a platform for the sale and purchase of developer assets and products. By listing items for sale on our platform, you agree to the following terms:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">You have the legal right to sell the items you list.</li>
                    <li className="text-white/90 leading-relaxed">Your listings must accurately describe the items being sold.</li>
                    <li className="text-white/90 leading-relaxed">You are responsible for fulfilling orders and providing support for your products.</li>
                    <li className="text-white/90 leading-relaxed">Developer Marketplace will collect a commission on each sale as outlined in our Seller Agreement.</li>
                  </ul>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    As a buyer on our platform, you acknowledge that:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">Developer Marketplace is not responsible for the quality, safety, or legality of items sold by users.</li>
                    <li className="text-white/90 leading-relaxed">We do not guarantee the accuracy of listings.</li>
                    <li className="text-white/90 leading-relaxed">Risk of loss and title for items purchased passes to you upon completion of the sale.</li>
                  </ul>
                </section>

                <section id="termination">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Termination</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.
                  </p>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service or contact us to request account deletion.
                  </p>
                </section>

                <section id="liability">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Limitation of Liability</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    In no event shall Developer Marketplace, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">Your access to or use of or inability to access or use the Service;</li>
                    <li className="text-white/90 leading-relaxed">Any conduct or content of any third party on the Service;</li>
                    <li className="text-white/90 leading-relaxed">Any content obtained from the Service; and</li>
                    <li className="text-white/90 leading-relaxed">Unauthorized access, use or alteration of your transmissions or content, whether based on warranty, contract, tort (including negligence) or any other legal theory, whether or not we have been informed of the possibility of such damage.</li>
                  </ul>
                </section>

                <section id="governing-law">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Governing Law</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    These Terms shall be governed and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
                  </p>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect.
                  </p>
                </section>

                <section id="changes">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Changes to Terms</h2>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    We reserve the right, at our sole discretion, to modify or replace these Terms at any time. We will provide notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
                  </p>
                  <p className="text-white/90 leading-relaxed mb-6 text-base">
                    By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the Service.
                  </p>
                </section>

                <section id="contact">
                  <h2 className="text-2xl text-white font-bold mt-8 mb-4 tracking-tight">Contact Us</h2>
                  <p className="text-white/90 leading-relaxed mb-4 text-base">
                    If you have any questions about these Terms, please contact us:
                  </p>
                  <ul className="list-disc pl-6 mb-6 space-y-2">
                    <li className="text-white/90 leading-relaxed">By email: <span className="text-[#F670C7]">legal@developermarketplace.com</span></li>
                    <li className="text-white/90 leading-relaxed">By visiting the <span className="text-[#F670C7] hover:text-[#F670C7]/80 hover:underline font-medium">contact page</span> on our website</li>
                  </ul>
                </section>

                <div className="mt-12 bg-[#2A1E44]/70 p-8 rounded-xl backdrop-blur-sm border border-white/10 shadow-lg">
                  <div className="flex flex-col md:flex-row items-start md:items-center space-y-4 md:space-y-0">
                    <div className="flex-shrink-0 h-12 w-12 bg-white/10 rounded-full flex items-center justify-center mr-0 md:mr-6">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#F670C7]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-white mb-2">Terms Acceptance</h3>
                      <p className="text-white/90 leading-relaxed mb-0 text-base">
                        By using the Developer Marketplace platform, you confirm that you have read, understood, and agree to be bound by these Terms and Conditions.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
